import cx from 'clsx';
import { defineMessages, useIntl } from 'react-intl';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import config from '@plone/volto/registry';
import {
  isExternalHref,
  resolveImageSrcSet,
  resolveImageUrl,
  resolveLinkHref,
  type ImageFieldValue,
  type LinkFieldValue,
} from '../../../helpers/blocks';

const messages = defineMessages({
  defaultTitle: {
    id: 'featurePanel.default.title',
    defaultMessage: 'Panel title',
  },
});

// A imagem ocupa 29% do container de 1130px (~330px) no desktop e a largura
// toda no mobile (< 769px) — ver _feature-panel.scss.
const MEDIA_SCALES = ['preview', 'teaser', 'large', 'larger'];
const MEDIA_SIZES = '(max-width: 768px) 100vw, 330px';

export type ImagePosition = 'left' | 'right';
export type ColorVariant = 'teal' | 'navy' | 'blue' | 'gold';

export type FeaturePanelData = {
  '@type'?: string;
  title?: string;
  /** Legado (pré-container): texto corrido com \n\n entre parágrafos. */
  text?: string;
  blocks?: Record<string, unknown>;
  blocks_layout?: { items: string[] };
  ctaLabel?: string;
  ctaLink?: LinkFieldValue;
  imagePosition?: ImagePosition;
  colorVariant?: ColorVariant;
  image?: ImageFieldValue;
  imageAlt?: string;
};

/**
 * Moldura compartilhada entre View e Edit: imagem 29% + painel de cor 71%,
 * radius 16 só nos cantos externos, sobre faixa cinza full-bleed
 * (spec feature-panel.md §12). `children` é a área de texto (RenderBlocks
 * na view, ContainerEdit no edit).
 */
export const FeaturePanelFrame = ({
  data,
  children,
  cta,
}: {
  data: FeaturePanelData;
  children: React.ReactNode;
  cta?: React.ReactNode;
}) => {
  const intl = useIntl();
  const imagePosition: ImagePosition = data.imagePosition || 'left';
  const colorVariant: ColorVariant = data.colorVariant || 'teal';
  const imageUrl = resolveImageUrl(data.image, 'large');
  const imageSrcSet = resolveImageSrcSet(data.image, MEDIA_SCALES);

  return (
    <div className="block-feature-panel">
      <div
        className={cx(
          'feature-panel',
          `feature-panel--${colorVariant}`,
          `feature-panel--image-${imagePosition}`,
        )}
      >
        <div className="feature-panel__media">
          {imageUrl ? (
            <img
              src={imageUrl}
              srcSet={imageSrcSet}
              sizes={imageSrcSet && MEDIA_SIZES}
              alt={data.imageAlt || ''}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="feature-panel__media--placeholder" />
          )}
        </div>

        <div className="feature-panel__panel">
          <h2 className="feature-panel__title">
            {data.title || intl.formatMessage(messages.defaultTitle)}
          </h2>
          <div className="feature-panel__body">{children}</div>
          {cta}
        </div>
      </div>
    </div>
  );
};

/** Legado: painéis salvos antes do container guardam o texto em `text`. */
export const LegacyText = ({ text }: { text: string }) => (
  <>
    {text.split(/\n{2,}/).map((paragraph, i) => (
      <p className="feature-panel__text" key={i}>
        {paragraph}
      </p>
    ))}
  </>
);

const FeaturePanelView = (props: {
  data: FeaturePanelData;
  path?: string;
  [key: string]: unknown;
}) => {
  const { data, path } = props;
  const metadata = props.metadata || props.properties;
  const ctaHref = resolveLinkHref(data.ctaLink);
  const hasNestedBlocks = Boolean(data.blocks_layout?.items?.length);

  const blocksConfig =
    (data['@type'] &&
      config.blocks.blocksConfig[data['@type']]?.blocksConfig) ||
    props.blocksConfig;

  return (
    <FeaturePanelFrame
      data={data}
      cta={
        // Sem link, sem botão — nada de `href="#"` no site (o editor continua
        // mostrando o rótulo, ver FeaturePanelEdit).
        data.ctaLabel &&
        ctaHref && (
          <a
            className="btn feature-panel__cta"
            href={ctaHref}
            {...(isExternalHref(ctaHref) && {
              target: '_blank',
              rel: 'noopener noreferrer',
            })}
          >
            {data.ctaLabel}
          </a>
        )
      }
    >
      {hasNestedBlocks ? (
        <RenderBlocks
          {...props}
          metadata={metadata}
          content={data}
          location={{ pathname: path }}
          blocksConfig={blocksConfig}
          isContainer
        />
      ) : (
        data.text && <LegacyText text={data.text} />
      )}
    </FeaturePanelFrame>
  );
};

export default FeaturePanelView;
